import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';

const PatientMemory: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('anchors');

    // Mock memory data
    const memoryData = {
        anchors: [
            {
                id: 1,
                title: 'Morning Gratitude',
                content: 'I am grateful for the small moments of peace in my day',
                emotion: 'grateful',
                date: '2024-01-15',
                tags: ['gratitude', 'morning', 'peace'],
                strength: 8
            },
            {
                id: 2,
                title: 'Overcoming Anxiety',
                content: 'I successfully managed my anxiety during the presentation by using breathing techniques',
                emotion: 'proud',
                date: '2024-01-12',
                tags: ['anxiety', 'success', 'breathing'],
                strength: 9
            },
            {
                id: 3,
                title: 'Family Connection',
                content: 'Had a meaningful conversation with my sister about our childhood memories',
                emotion: 'connected',
                date: '2024-01-10',
                tags: ['family', 'connection', 'memories'],
                strength: 7
            },
            {
                id: 4,
                title: 'Work Achievement',
                content: 'Completed the project ahead of schedule and received positive feedback',
                emotion: 'accomplished',
                date: '2024-01-08',
                tags: ['work', 'achievement', 'feedback'],
                strength: 9
            }
        ],
        insights: [
            {
                id: 1,
                title: 'Pattern Recognition',
                content: 'You tend to feel most grateful in the morning hours',
                type: 'pattern',
                confidence: 85
            },
            {
                id: 2,
                title: 'Growth Area',
                content: 'Your anxiety management techniques are becoming more effective over time',
                type: 'growth',
                confidence: 92
            },
            {
                id: 3,
                title: 'Emotional Insight',
                content: 'Family connections significantly boost your emotional well-being',
                type: 'insight',
                confidence: 78
            }
        ],
        stats: {
            totalAnchors: 24,
            thisMonth: 8,
            averageStrength: 7.5,
            topEmotion: 'grateful'
        }
    };

    const tabs = [
        { id: 'anchors', label: 'Memory Anchors', icon: '🎯' },
        { id: 'insights', label: 'AI Insights', icon: '🧠' },
        { id: 'patterns', label: 'Patterns', icon: '📊' }
    ];

    const getEmotionColor = (emotion: string) => {
        const colors: { [key: string]: string } = {
            grateful: 'bg-green-100 text-green-800',
            proud: 'bg-blue-100 text-blue-800',
            connected: 'bg-purple-100 text-purple-800',
            accomplished: 'bg-yellow-100 text-yellow-800',
            happy: 'bg-pink-100 text-pink-800',
            calm: 'bg-indigo-100 text-indigo-800'
        };
        return colors[emotion] || 'bg-gray-100 text-gray-800';
    };

    const getStrengthColor = (strength: number) => {
        if (strength >= 8) return 'bg-green-500';
        if (strength >= 6) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Memory Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-2xl">🧠</span>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Memory & Anchors</h1>
                            <p className="text-gray-600">Capture meaningful moments and discover patterns in your emotional journey</p>
                        </div>
                    </div>
                </div>
            
                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 text-sm">🎯</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Total Anchors</p>
                                <p className="text-2xl font-semibold text-gray-900">{memoryData.stats.totalAnchors}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-green-600 text-sm">📅</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">This Month</p>
                                <p className="text-2xl font-semibold text-gray-900">{memoryData.stats.thisMonth}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <span className="text-purple-600 text-sm">💪</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Avg Strength</p>
                                <p className="text-2xl font-semibold text-gray-900">{memoryData.stats.averageStrength}/10</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <span className="text-yellow-600 text-sm">😊</span>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Top Emotion</p>
                                <p className="text-2xl font-semibold text-gray-900 capitalize">{memoryData.stats.topEmotion}</p>
                            </div>
                        </div>
                    </div>
                </div>

            {/* Tab Navigation */}
            <PageSection title="Memory Tools" emoji="🛠️">
                <div className="flex flex-wrap gap-2 mb-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                activeTab === tab.id
                                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <span>{tab.icon}</span>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'anchors' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">Recent Memory Anchors</h3>
                            <button 
                                onClick={() => alert('Add Memory Anchor functionality coming soon!')}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                + Add New Anchor
                            </button>
                        </div>
                        <div className="space-y-4">
                            {memoryData.anchors.map((anchor) => (
                                <div key={anchor.id} className="p-6 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{anchor.title}</h4>
                                            <p className="text-gray-700 mb-3">{anchor.content}</p>
                                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                <span>{anchor.date}</span>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmotionColor(anchor.emotion)}`}>
                                                    {anchor.emotion}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="text-right">
                                                <div className="text-sm font-medium text-gray-900">Strength</div>
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                        <div 
                                                            className={`h-2 rounded-full ${getStrengthColor(anchor.strength)}`}
                                                            style={{ width: `${(anchor.strength / 10) * 100}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm font-medium">{anchor.strength}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {anchor.tags.map((tag, index) => (
                                            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'insights' && (
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900">AI-Generated Insights</h3>
                        <div className="space-y-4">
                            {memoryData.insights.map((insight) => (
                                <div key={insight.id} className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0">
                                            <span className="text-2xl">
                                                {insight.type === 'pattern' ? '📊' : insight.type === 'growth' ? '🌱' : '💡'}
                                            </span>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">{insight.title}</h4>
                                            <p className="text-gray-700 mb-3">{insight.content}</p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-500">Confidence: {insight.confidence}%</span>
                                                <div className="flex space-x-2">
                                                    <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm">
                                                        Learn More
                                                    </button>
                                                    <button className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm">
                                                        Apply Insight
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'patterns' && (
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900">Emotional Patterns</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-6 bg-white border border-gray-200 rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-4">Emotion Distribution</h4>
                                <div className="space-y-3">
                                    {['grateful', 'proud', 'connected', 'accomplished'].map((emotion, index) => (
                                        <div key={emotion} className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700 capitalize">{emotion}</span>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                                    <div 
                                                        className="bg-blue-500 h-2 rounded-full" 
                                                        style={{ width: `${(index + 1) * 20}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-sm text-gray-500">{(index + 1) * 20}%</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="p-6 bg-white border border-gray-200 rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-4">Strength Trends</h4>
                                <div className="space-y-3">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">+15%</div>
                                        <div className="text-sm text-gray-600">vs Last Month</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">7.5</div>
                                        <div className="text-sm text-gray-600">Average Strength</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </PageSection>

            {/* Quick Actions */}
            <PageSection title="Quick Actions" emoji="⚡">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button 
                        onClick={() => alert('Add Memory Anchor functionality coming soon!')}
                        className="p-4 bg-purple-100 hover:bg-purple-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">➕</span>
                            <div>
                                <div className="font-medium text-gray-900">Add Memory Anchor</div>
                                <div className="text-sm text-gray-600">Capture a meaningful moment</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => alert('Search Memories functionality coming soon!')}
                        className="p-4 bg-blue-100 hover:bg-blue-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">🔍</span>
                            <div>
                                <div className="font-medium text-gray-900">Search Memories</div>
                                <div className="text-sm text-gray-600">Find specific moments</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => navigate('/analytics')}
                        className="p-4 bg-green-100 hover:bg-green-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📊</span>
                            <div>
                                <div className="font-medium text-gray-900">View Analytics</div>
                                <div className="text-sm text-gray-600">See detailed patterns</div>
                            </div>
                        </div>
                    </button>
                </div>
            </PageSection>
            </div>
        </div>
    );
};

export default PatientMemory;
